import { NuplayWordmark } from "./components/NuplayWordmark";
import { useNavigate } from "react-router-dom";

// Copa-branded entry. The two scenarios are the primary CTAs; each navigates
// into the live COPA Crew experience (→ channel pick → the call dashboard).
const SCENARIOS = [
  {
    persona: "copa_sofia",
    query: "refund-status",
    badge: "Refund + Rebooking",
    badgeBg: "rgba(59,130,246,0.12)",
    badgeBorder: "rgba(59,130,246,0.25)",
    badgeText: "var(--nx-blue-text)",
    title: "Compassionate refund status",
    desc: "Sofia cancelled her Panama City → New York trip after a bereavement. The agent verifies her, explains the refund case with empathy, captures new travel intent, and hands off to a specialist — never approving the refund itself.",
    cta: "Start Sofia's call",
    gradient: "from-[#2563eb] to-[#0ea5e9]",
  },
  {
    persona: "copa_ana",
    query: "baggage-reimbursement",
    badge: "Baggage + Voucher",
    badgeBg: "rgba(13,148,136,0.12)",
    badgeBorder: "rgba(13,148,136,0.25)",
    badgeText: "var(--nx-green-text)",
    title: "Delayed baggage reimbursement",
    desc: "Ana's bag arrived two days late on her Bogotá → Miami trip. The agent pulls her baggage claim, reimbursement case, and voucher, resends what's missing, and escalates approval to the right team.",
    cta: "Start Ana's call",
    gradient: "from-[#0d9488] to-[#10b981]",
  },
];

const CREW = [
  { name: "Routing", desc: "Greets & routes" },
  { name: "Refund & Rebooking", desc: "Refund + handoff" },
  { name: "Baggage & Voucher", desc: "Claims + reimbursement" },
  { name: "Human Transfer", desc: "Warm escalation" },
];

export default function CopaLandingPage() {
  const navigate = useNavigate();
  const start = (persona: string, query: string) =>
    navigate(`/nurix-experience/experience?agent=copa-airlines&persona=${persona}&query=${query}`);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--nx-bg)", fontFamily: "Nunito, system-ui, sans-serif" }}>
      {/* Background atmosphere */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="orb animate-orb_float" style={{ width: 700, height: 700, top: "-15%", right: "-10%", background: "radial-gradient(circle, rgba(37,99,235,0.14) 0%, transparent 70%)" }} />
        <div className="orb" style={{ width: 500, height: 500, bottom: "5%", left: "-8%", background: "radial-gradient(circle, rgba(14,165,233,0.10) 0%, transparent 70%)", animationDelay: "3s" }} />
      </div>

      {/* Nav */}
      <nav className="sticky top-0 z-30 flex-shrink-0" style={{ background: "var(--nx-nav)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderBottom: "1px solid var(--nx-border)" }}>
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg,#1d4ed8,#0ea5e9)" }}>
              <span className="text-white font-extrabold text-[13px] tracking-tight">C</span>
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-[15px] font-bold tracking-tight" style={{ color: "var(--nx-text-1)" }}>Copa Airlines</span>
              <span className="text-[9px] font-semibold tracking-[0.16em] uppercase" style={{ color: "var(--nx-text-3)" }}>Passenger Care · NuPlay AI</span>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-1.5 rounded-full text-[12px] font-medium" style={{ background: "rgba(52,211,153,0.10)", border: "1px solid rgba(52,211,153,0.20)", color: "var(--color-success)" }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--color-success)" }} />
            Crew Online
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="relative flex-1">
        <div className="relative max-w-5xl mx-auto px-6 py-16 md:py-20">
          <div className="step-enter text-center max-w-2xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-[0.09em] uppercase mb-6" style={{ background: "rgba(37,99,235,0.12)", border: "1px solid rgba(37,99,235,0.25)", color: "var(--nx-blue-text)" }}>
              <span className="w-1 h-1 rounded-full" style={{ background: "var(--nx-blue-text)" }} />
              Live voice agent demo
            </div>
            <h1 className="text-[40px] md:text-[50px] font-extrabold leading-[1.08] tracking-[-0.035em] mb-5" style={{ color: "var(--nx-text-1)" }}>
              Care that resolves —{" "}
              <span style={{ background: "linear-gradient(135deg,#ffffff 0%,#93c5fd 45%,#38bdf8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                for every passenger.
              </span>
            </h1>
            <p className="text-[16px] leading-relaxed" style={{ color: "var(--nx-text-2)" }}>
              A Copa Airlines voice crew that verifies passengers, explains refund and baggage cases live from real booking data, and hands off to a specialist with full context.
            </p>
          </div>

          {/* Scenario cards */}
          <div className="grid md:grid-cols-2 gap-5 mb-12">
            {SCENARIOS.map((s) => (
              <button
                key={s.persona}
                onClick={() => start(s.persona, s.query)}
                className="group text-left rounded-2xl p-7 transition-all duration-150 active:scale-[0.99]"
                style={{ background: "var(--nx-card-bg)", border: "1px solid var(--nx-card-border)" }}
                onMouseEnter={(e) => { const t = e.currentTarget as HTMLButtonElement; t.style.borderColor = "var(--nx-hover-border)"; t.style.boxShadow = "0 12px 40px rgba(0,0,0,0.22)"; }}
                onMouseLeave={(e) => { const t = e.currentTarget as HTMLButtonElement; t.style.borderColor = "var(--nx-card-border)"; t.style.boxShadow = "none"; }}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{ background: s.badgeBg, border: `1px solid ${s.badgeBorder}`, color: s.badgeText }}>{s.badge}</span>
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${s.gradient} flex items-center justify-center shadow-lg`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
                  </div>
                </div>
                <h3 className="text-[19px] font-bold mb-2" style={{ color: "var(--nx-text-1)" }}>{s.title}</h3>
                <p className="text-[13px] leading-relaxed mb-5" style={{ color: "var(--nx-text-2)" }}>{s.desc}</p>
                <span className="inline-flex items-center gap-2 text-[14px] font-semibold" style={{ color: "var(--nx-accent-text)" }}>
                  {s.cta}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="transition-transform duration-150 group-hover:translate-x-1"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </span>
              </button>
            ))}
          </div>

          {/* Crew strip */}
          <div className="rounded-2xl p-6" style={{ background: "var(--nx-card-bg)", border: "1px solid var(--nx-card-border)" }}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.09em] mb-4 text-center" style={{ color: "var(--nx-text-3)" }}>Powered by a 4-agent crew</div>
            <div className="flex items-center justify-center gap-1.5 flex-wrap">
              {CREW.map((c, i) => (
                <div key={c.name} className="flex items-center gap-1.5">
                  {i > 0 && (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="opacity-40 flex-shrink-0"><path d="M4 8h8M9 5l3 3-3 3" stroke="var(--nx-text-3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  )}
                  <div className="px-3 py-2 rounded-xl text-center" style={{ background: "var(--nx-hover)", border: "1px solid var(--nx-border)" }}>
                    <div className="text-[12px] font-semibold whitespace-nowrap" style={{ color: "var(--nx-text-1)" }}>{c.name}</div>
                    <div className="text-[10px] whitespace-nowrap" style={{ color: "var(--nx-text-3)" }}>{c.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{ borderTop: "1px solid var(--nx-border)" }}>
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <span className="text-[13px]" style={{ color: "var(--nx-text-3)" }}>Copa Airlines · Passenger care demo</span>
          <span className="text-[13px]" style={{ color: "var(--nx-text-3)" }}>
            Powered by <span style={{ color: "var(--nx-text-1)", display: "inline-flex" }}><NuplayWordmark height={16} /></span>
          </span>
        </div>
      </footer>
    </div>
  );
}
